import { useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { DataTable, type Column } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDate, formatNumber } from '@/i18n/format';
import type { ReturnRow } from '@/api/types';

const LIMIT = 50;

export function ReturnsListPage(): JSX.Element {
  const [params] = useSearchParams();
  const [awaitingOnly, setAwaitingOnly] = useState(params.get('awaitingDispositionOnly') === 'true');
  const [disposition, setDisposition] = useState('');
  const [offset, setOffset] = useState(0);
  const result = useApi(
    () => endpoints.returns({ awaitingDispositionOnly: awaitingOnly, disposition: disposition || undefined, limit: LIMIT, offset }),
    [awaitingOnly, disposition, offset],
  );
  const { t, locale } = useLanguage();

  const columns: readonly Column<ReturnRow>[] = [
    { key: 'number', header: t('returns.colNumber'), render: (r) => r.number },
    { key: 'owner', header: t('inbounds.colSeller'), render: (r) => r.owner_name },
    { key: 'received', header: t('inbounds.colReceived'), render: (r) => formatNumber(r.qty_received, locale), align: 'right' },
    { key: 'dispositioned', header: t('returns.colDispositioned'), render: (r) => formatNumber(r.qty_dispositioned, locale), align: 'right' },
    {
      key: 'unresolved',
      header: t('returns.colUnresolved'),
      render: (r) => {
        const unresolved = Number(r.qty_received) - Number(r.qty_dispositioned);
        return unresolved > 0 ? <span className="badge warn">{formatNumber(unresolved, locale)}</span> : '0';
      },
      align: 'right',
    },
    { key: 'received_at', header: t('returns.colReceivedAt'), render: (r) => (r.arrived_at ? formatDate(r.arrived_at, locale) : '—') },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('nav.returns')}</h1>
      </div>
      <div className="filters">
        <label>
          <input
            type="checkbox"
            checked={awaitingOnly}
            onChange={(e) => {
              setAwaitingOnly(e.target.checked);
              setOffset(0);
            }}
          />
          {t('returns.awaitingDisposition')}
        </label>
        <select
          aria-label={t('returns.filterByDisposition')}
          value={disposition}
          onChange={(e) => {
            setDisposition(e.target.value);
            setOffset(0);
          }}
        >
          <option value="">{t('returns.allDispositions')}</option>
          <option value="RESTOCK">{t('status.RESTOCK')}</option>
          <option value="QUARANTINE">{t('status.QUARANTINE')}</option>
          <option value="WRITE_OFF">{t('status.WRITE_OFF')}</option>
        </select>
      </div>
      <DataTable
        result={result}
        columns={columns}
        getRowHref={(r) => `/returns/${r.id}`}
        limit={LIMIT}
        offset={offset}
        onPageChange={setOffset}
        emptyTitle={t('returns.emptyTitle')}
      />
    </div>
  );
}
